import type { Campaign } from '@mobi/account/Areas/SignUp'
import { FeatureFlags } from '@mobi/settings/featureFlags'
import { sendToNative } from '@mobi/web-native-comms/web'
import { getCampaigns as getCampaignsV2 } from '@core/Areas/Bonuses'
import { CampaignV2 } from '@core/Areas/Bonuses/data/types'
import { post } from '@core/Services/ApiService'
import { store } from '@core/Store'
import { getFeaturesIsUserAnonymous } from '../FeatureFlags/Store/selectors'
import { getFeatureValue } from '../FeatureFlags/helpers/getFeatureValue'
import { UpdateAccountBalance, UpdateBonusBalance, UpdateCampaigns } from './userAccountDriver'

export async function fetchAccountBalanceAsync(): Promise<void> {
  const { balance } = await getAccountBalance()

  UpdateAccountBalance({ balance })
  sendToNative('ACCOUNT_BALANCE', { balance })
}

export async function fetchCampaignsAsync(): Promise<void> {
  const response = await getCampaignsResponse()
  UpdateCampaigns(response.campaigns)
  UpdateBonusBalance({
    bonusBetBalance: response.bonusBetBalance,
    bonusCashBalance: response.bonusCashBalance,
  })
}

export interface GetAccountBalanceResponse {
  balance: number
  withdrawableBalance: number
}

export interface GetCampaignsResponse {
  bonusBetBalance: number
  bonusCashBalance: number
  campaigns: Campaign[]
}

function getAccountBalance(): Promise<GetAccountBalanceResponse> {
  return post<GetAccountBalanceResponse>({ url: '/api/account/balance' })
}

function getCampaigns(): Promise<GetCampaignsResponse> {
  return post<GetCampaignsResponse>({ url: '/api/account/campaigns' })
}

async function getCampaignsResponse(): Promise<GetCampaignsResponse> {
  const defaultValue = { campaigns: [], bonusBetBalance: 0.0, bonusCashBalance: 0.0 }

  const isUserAnonymous = getFeaturesIsUserAnonymous(store.getState())

  if (isUserAnonymous) {
    return defaultValue
  }

  const useCampaignsV2 = getFeatureValue(FeatureFlags.GET_CAMPAIGNS_V2.key)

  if (useCampaignsV2) {
    try {
      const bonusRewards = await getCampaignsV2()

      if (!bonusRewards) {
        return defaultValue
      }

      const sumAvailableAmount = (rewards: CampaignV2[], type: string): number => {
        if (rewards) {
          return rewards
            .filter(r => r?.rewardType === type)
            .map(r => r?.remainingAmount || 0)
            .reduce((a, b) => a + b, 0)
        }
        return 0
      }

      const bonusBetBalance = sumAvailableAmount(bonusRewards, 'BonusBetReward')
      const bonusCashBalance = sumAvailableAmount(bonusRewards, 'BonusCashReward')

      const campaignsV2: GetCampaignsResponse = {
        campaigns: bonusRewards,
        bonusBetBalance: bonusBetBalance,
        bonusCashBalance: bonusCashBalance,
      }

      return campaignsV2
    } catch {
      return defaultValue
    }
  }

  return await getCampaigns()
}
